<script lang="ts">
export interface PopoverProps extends DeprecatedPlacementProps, PlacementProps {
	disabled?: boolean;
	offset?: number;
	alignOffset?: number;
	arrow?: boolean;
	matchWidth?: boolean;
	keepMounted?: boolean;
	followCursor?: boolean;
	closeOnClick?: boolean;
}

export type PopoverEmits = {
	"update:open": [value: boolean];
};
</script>

<script setup lang="ts">
import { useEventListener } from "@vueuse/core";
import {
	PopoverAnchor,
	PopoverArrow,
	PopoverContent,
	PopoverPortal,
	PopoverRoot,
	PopoverTrigger,
	useForwardPropsEmits,
	type ReferenceElement,
} from "reka-ui";
import { computed, ref, watch } from "vue";
import {
	convertDeprecatedPlacement,
	type DeprecatedPlacementProps,
	type PlacementProps,
} from "../../lib/placement";

const props = withDefaults(defineProps<PopoverProps>(), { offset: 4 });
const emit = defineEmits<PopoverEmits>();

const forwarded = useForwardPropsEmits(props, emit);

const placement = computed<PlacementProps>(() => {
	if (props.side || props.align) {
		return { side: props.side, align: props.align };
	}

	return convertDeprecatedPlacement(props.placement ?? "bottom");
});

defineSlots<{
	trigger(args: { open: boolean }): void;
	default(args: { open: boolean }): void;
}>();

const openModel = defineModel<boolean>("open");

const anchor = ref({
	x: 0,
	y: 0,
});
const position = ref<ReferenceElement>();
watch(
	openModel,
	(open) => {
		if (open && props.followCursor) {
			position.value = {
				getBoundingClientRect: () =>
					({
						width: 0,
						height: 0,
						left: anchor.value.x,
						right: anchor.value.x,
						top: anchor.value.y,
						bottom: anchor.value.y,
						...anchor.value,
					}) as DOMRect,
			};
		}
	},
	{ immediate: true, flush: "sync" }
);

function handleClick() {
	if (props.closeOnClick && openModel.value) {
		openModel.value = false;
	}
}

useEventListener(
	"mousedown",
	({ clientX, clientY }) => (anchor.value = { x: clientX, y: clientY })
);
</script>

<template>
	<PopoverRoot v-slot="slotProps" v-model:open="openModel" v-bind="forwarded">
		<PopoverAnchor :reference="props.followCursor ? position : undefined" asChild>
			<PopoverTrigger asChild>
				<slot name="trigger" v-bind="slotProps" />
			</PopoverTrigger>
		</PopoverAnchor>

		<PopoverPortal>
			<PopoverContent
				v-show="!props.keepMounted || openModel"
				class="popover popover-animate"
				:forceMount="props.keepMounted"
				:align="placement.align"
				:alignOffset="alignOffset"
				:side="placement.side"
				:sideOffset="offset"
				:style="{
					minWidth: props.matchWidth ? 'var(--reka-popover-trigger-width)' : 0,
				}"
				avoidCollisions
				updatePositionStrategy="optimized"
				positionStrategy="fixed"
				@click="handleClick"
				@openAutoFocus.prevent
				@closeAutoFocus.prevent
			>
				<slot v-bind="slotProps" />
				<PopoverArrow v-if="arrow" />
			</PopoverContent>
		</PopoverPortal>
	</PopoverRoot>
</template>
